import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  useEffect(() => {
    document.title = 'Contact Us - Nova Global Supply';
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    productInterest: '',
    projectType: '',
    projectDetails: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, productInterest: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success("Message Sent!", {
          description: response.data.message,
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          role: '',
          productInterest: '',
          projectType: '',
          projectDetails: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error("Error", {
        description: "Failed to submit contact request. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col">
      {/* Header Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
              Get In Touch
            </span>
          </h1>
          <p className="text-lg text-slate-600">
            Have questions about our products? Need technical specifications? We're here to help.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center mb-2">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-lg">Email Us</CardTitle>
                  <CardDescription>We'll respond within 24 hours</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">Use the form to send us a message</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center mb-2">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-lg">Call Us</CardTitle>
                  <CardDescription>Available upon request</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">Contact us for phone support</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center mb-2">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-lg">Location</CardTitle>
                  <CardDescription>Serving all of Canada</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">Nationwide shipping and support</p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Request Pricing & Information</CardTitle>
                  <CardDescription>
                    Please provide detailed information about your project for accurate pricing and faster response
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Personal Information Section */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Your Information</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="John Smith"
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@example.com"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone Number *</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="(555) 123-4567"
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="company">Company Name *</Label>
                          <Input
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Your Company Name"
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="role">Your Role *</Label>
                        <Select value={formData.role} onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="contractor">General Contractor</SelectItem>
                            <SelectItem value="builder">Builder/Developer</SelectItem>
                            <SelectItem value="engineer">Engineer</SelectItem>
                            <SelectItem value="architect">Architect</SelectItem>
                            <SelectItem value="inspector">Building Inspector</SelectItem>
                            <SelectItem value="plumber">Plumber</SelectItem>
                            <SelectItem value="procurement">Procurement Manager</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Project Information Section */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Project Information</h3>
                      
                      <div className="space-y-2">
                        <Label htmlFor="productInterest">Product Interest *</Label>
                        <Select value={formData.productInterest} onValueChange={handleSelectChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select product(s) you're interested in" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="versaduct">VersaDuct Return Air Liner</SelectItem>
                            <SelectItem value="nova-faucets">Nova Faucets</SelectItem>
                            <SelectItem value="versaflex-csst">VersaFlex CSST</SelectItem>
                            <SelectItem value="nova-floors">Nova Floors</SelectItem>
                            <SelectItem value="multiple">Multiple Products</SelectItem>
                            <SelectItem value="all-products">All Products</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="projectType">Project Type *</Label>
                        <Select value={formData.projectType} onValueChange={(value) => setFormData(prev => ({ ...prev, projectType: value }))}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select project type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="residential-new">Residential - New Construction</SelectItem>
                            <SelectItem value="residential-renovation">Residential - Renovation</SelectItem>
                            <SelectItem value="commercial-new">Commercial - New Construction</SelectItem>
                            <SelectItem value="commercial-renovation">Commercial - Renovation</SelectItem>
                            <SelectItem value="multi-unit">Multi-Unit Development</SelectItem>
                            <SelectItem value="industrial">Industrial</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="projectDetails">Project Details *</Label>
                        <Textarea
                          id="projectDetails"
                          name="projectDetails"
                          value={formData.projectDetails}
                          onChange={handleChange}
                          placeholder="Please be specific: number of units, square footage, timeline, quantities needed, specific products from catalog, etc."
                          rows={4}
                          required
                        />
                        <p className="text-xs text-slate-500">
                          The more details you provide (quantities, timeline, specific models), the better we can serve you with accurate pricing
                        </p>
                      </div>
                    </div>

                    {/* Additional Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message">Additional Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Any additional information, questions, or special requirements..."
                        rows={4}
                      />
                    </div>

                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                      <p className="text-sm text-slate-700">
                        <strong>💡 Tip:</strong> Providing detailed project information helps us give you more accurate pricing and faster responses. Include quantities, timelines, and specific product requirements.
                      </p>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white shadow-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Submit Request
                          <Send className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="container mx-auto max-w-4xl">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">What to Expect</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-slate-900 mb-2">24 Hours</div>
                  <p className="text-sm text-slate-600">Response time for all inquiries</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-slate-900 mb-2">Expert</div>
                  <p className="text-sm text-slate-600">Technical support staff</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-slate-900 mb-2">Complete</div>
                  <p className="text-sm text-slate-600">Product documentation provided</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Contact;